/**
 * Profile API: fetch profile and sync customer (including allergies/dislikes) to auth store.
 * Customer's selected allergies and dislikes come from the profile response.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiGet, apiPost, apiPostFormData } from '../../../shared/api/client';
import { endpoints } from '../../../shared/config/endpoints';
import { getAuthStore } from '../../../shared/auth/store';
import type { Allergy, Dislike, User } from './types';

const CURRENT_USER_KEY = 'currentUser';

export interface ProfileResponse {
  success?: boolean;
  data?: { customer?: any };
  customer?: any;
}

function parseIdFromRaw(a: any): number | null {
  if (typeof a === 'number' && Number.isFinite(a)) {return a;}
  if (typeof a === 'string') {
    const n = Number(a);
    if (Number.isFinite(n)) {return n;}
  }
  return null;
}

function normalizeProfileHealthItem(
  raw: any,
  alternate_id_key: 'allergy_id' | 'dislike_id',
): Allergy | Dislike | null {
  if (typeof raw === 'number') {
    return { id: raw, slug: '', title: '', description: '', is_active: true };
  }
  const sid = parseIdFromRaw(raw);
  const id =
    sid ??
    (raw && typeof raw.id === 'number' ? raw.id : null) ??
    (raw && typeof raw[alternate_id_key] === 'number' ? raw[alternate_id_key] : null);
  if (id == null) {
    return null;
  }
  return {
    id,
    slug: raw?.slug != null ? String(raw.slug) : '',
    title: raw?.title != null ? String(raw.title) : '',
    description: raw?.description != null ? String(raw.description) : '',
    is_active: raw?.is_active !== false,
  };
}

/** Normalize allergies from profile: may be array of ids or array of { id, title?, ... } */
function normalizeAllergies(raw: any): Allergy[] {
  if (!raw) {return [];}
  if (Array.isArray(raw)) {
    return raw
      .map((a: any) => normalizeProfileHealthItem(a, 'allergy_id'))
      .filter(Boolean) as Allergy[];
  }
  return [];
}

/** Normalize dislikes from profile: may be array of ids or array of { id, title?, ... } */
function normalizeDislikes(raw: any): Dislike[] {
  if (!raw) {return [];}
  if (Array.isArray(raw)) {
    return raw
      .map((d: any) => normalizeProfileHealthItem(d, 'dislike_id'))
      .filter(Boolean) as Dislike[];
  }
  return [];
}

/**
 * Prefer non-empty `allergy_ids`, else non-empty `allergies`, else whichever array is defined (including []).
 * Avoids `allergies ?? allergy_ids` when `allergies` is `[]` but `allergy_ids` has values.
 */
function coalesceAllergyRaw(customer: any): any {
  const ids = customer?.allergy_ids;
  const list = customer?.allergies;
  if (Array.isArray(ids) && ids.length > 0) {return ids;}
  if (Array.isArray(list) && list.length > 0) {return list;}
  if (Array.isArray(ids)) {return ids;}
  if (Array.isArray(list)) {return list;}
  return ids ?? list ?? [];
}

function coalesceDislikeRaw(customer: any): any {
  const ids = customer?.dislike_ids;
  const list = customer?.dislikes;
  if (Array.isArray(ids) && ids.length > 0) {return ids;}
  if (Array.isArray(list) && list.length > 0) {return list;}
  if (Array.isArray(ids)) {return ids;}
  if (Array.isArray(list)) {return list;}
  return ids ?? list ?? [];
}

/** Backend may expose email as `mail`, `email`, or only inside `preferences` JSON. */
function resolveMailFromCustomerPayload(customer_raw: any): string {
  const top = customer_raw?.mail ?? customer_raw?.email;
  if (top != null && String(top).trim() !== '') {
    return String(top).trim();
  }
  const pr = customer_raw?.preferences;
  if (pr == null || pr === '') {
    return '';
  }
  try {
    const p = typeof pr === 'string' ? JSON.parse(pr) : pr;
    const inner = p?.mail ?? p?.email;
    return inner != null && String(inner).trim() !== '' ? String(inner).trim() : '';
  } catch {
    return '';
  }
}

function resolvePhoneFromCustomerPayload(customer_raw: any): string {
  const top = customer_raw?.phone;
  if (top != null && String(top).trim() !== '') {
    return String(top).trim();
  }
  const pr = customer_raw?.preferences;
  if (pr == null || pr === '') {
    return '';
  }
  try {
    const p = typeof pr === 'string' ? JSON.parse(pr) : pr;
    const inner = p?.phone;
    return inner != null && String(inner).trim() !== '' ? String(inner).trim() : '';
  } catch {
    return '';
  }
}

/**
 * First-name field: prefer API `first_name`. If only `name` + `last_name` exist, avoid putting the full
 * display name in the first-name slot (prevents "Shakir Ali" + "Ali").
 */
export function resolveProfileFirstName(customer: any): string {
  const fn = customer?.first_name;
  if (fn != null && String(fn).trim() !== '') {
    return String(fn).trim();
  }
  const full = customer?.name != null ? String(customer.name).trim() : '';
  if (full === '') {
    return '';
  }
  const ln = customer?.last_name != null ? String(customer.last_name).trim() : '';
  if (ln === '') {
    return full;
  }
  const lower_full = full.toLowerCase();
  const lower_ln = ln.toLowerCase();
  if (lower_full.endsWith(lower_ln) && full.length > ln.length) {
    const without = full.slice(0, full.length - ln.length).trim();
    if (without !== '') {
      return without;
    }
  }
  const parts = full.split(/\s+/).filter(Boolean);
  return parts[0] ?? '';
}

/** Last-name field: prefer API `last_name`; else remainder of `name` after the first word. */
export function resolveProfileLastName(customer: any): string {
  const ln = customer?.last_name;
  if (ln != null && String(ln).trim() !== '') {
    return String(ln).trim();
  }
  const full = customer?.name != null ? String(customer.name).trim() : '';
  if (full === '') {
    return '';
  }
  const parts = full.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return '';
  }
  return parts.slice(1).join(' ');
}

/** POST body path per API: `customer/profile/update` with multipart fields (Postman). */
export const PROFILE_UPDATE_ENDPOINT = `${endpoints.profile}/update`;

export type ProfileScreenUpdateFields = {
  first_name: string;
  last_name: string;
  mail: string;
  phone: string;
  gender: string;
  height?: string;
  weight?: string;
  date_of_birth?: string;
};

/**
 * Build multipart body for profile/update: `name`, `first_name`, `last_name`, optional `gender`,
 * and extended fields inside `preferences` JSON (`mail`, `phone`, height, etc.).
 */
export function buildProfileScreenProfileUpdateFormData(
  customer_id: number,
  fields: ProfileScreenUpdateFields,
  existing_preferences_raw?: unknown,
): FormData {
  const fd = new FormData();
  fd.append('customer_id', String(customer_id));

  const first = fields.first_name.trim();
  const last = fields.last_name.trim();
  fd.append('first_name', first);
  fd.append('last_name', last);
  const full = [first, last].filter(Boolean).join(' ').trim();
  if (full !== '') {
    fd.append('name', full);
  }

  const g = fields.gender.trim();
  if (g === 'male' || g === 'female') {
    fd.append('gender', g);
  }

  let prefs: Record<string, unknown> = {};
  if (existing_preferences_raw != null && String(existing_preferences_raw) !== '') {
    try {
      const parsed =
        typeof existing_preferences_raw === 'string'
          ? JSON.parse(existing_preferences_raw as string)
          : existing_preferences_raw;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        prefs = { ...(parsed as Record<string, unknown>) };
      }
    } catch {
      // keep prefs empty
    }
  }

  prefs.mail = fields.mail.trim();
  prefs.phone = fields.phone.trim();
  if (fields.height != null && String(fields.height).trim() !== '') {
    prefs.height = fields.height;
  }
  if (fields.weight != null && String(fields.weight).trim() !== '') {
    prefs.weight = fields.weight;
  }
  if (fields.date_of_birth != null && String(fields.date_of_birth).trim() !== '') {
    prefs.date_of_birth = fields.date_of_birth;
  }

  fd.append('preferences', JSON.stringify(prefs));
  return fd;
}

export async function postCustomerProfileUpdate(formData: FormData): Promise<any> {
  return apiPostFormData(PROFILE_UPDATE_ENDPOINT, formData);
}

export async function postDeviceTokenForProfile(customer_id: number, device_token: string): Promise<boolean> {
  const fd = new FormData();
  fd.append('customer_id', String(customer_id));
  fd.append('device_token', device_token);
  const res = (await apiPostFormData(PROFILE_UPDATE_ENDPOINT, fd)) as { success?: boolean };
  return res?.success !== false;
}

export async function postCustomerProfileImage(formData: FormData): Promise<any> {
  return apiPostFormData(endpoints.profile, formData);
}

export async function deactivateAccount(customer_id: number): Promise<any> {
  return apiPost(endpoints.deactivate, { customer_id });
}

export async function deleteAccount(customer_id: number): Promise<any> {
  return apiPost(endpoints.deleteAccount, { customer_id });
}

export type UpdatePasswordPayload = {
  phone: string;
  country_iso_code: string;
  reset_password_token: string;
  password: string;
  password_confirmation: string;
  otp_confirmed: boolean;
};

export async function updatePassword(payload: UpdatePasswordPayload): Promise<{ success?: boolean; message?: string }> {
  return apiPost(endpoints.updatePassword, payload);
}

export async function requestPasswordReset(phone: string): Promise<{ success?: boolean; message?: string }> {
  return apiPost(endpoints.resetPassword, { phone });
}

/** Fetch profile customer payload without syncing auth store (e.g. loyalty referral code). */
export async function fetchProfileCustomer(customer_id: number): Promise<any | null> {
  const res = await apiGet<ProfileResponse>(endpoints.profile, { customer_id });
  return (res as any)?.data?.customer ?? (res as any)?.customer ?? null;
}

/** Dashboard / internal profile/update payloads (legacy JSON) → multipart form. */
export type ProfileUpdateFormPayload = {
  customer_id: number;
  name?: string;
  first_name?: string;
  last_name?: string;
  mail?: string;
  email?: string;
  phone?: string;
  gender?: string;
  height?: string;
  weight?: string;
  date_of_birth?: string;
  preferences?: string;
  bmi?: number;
  internal_notes?: string;
  [key: string]: unknown;
};

const DASHBOARD_PREF_KEYS = ['mail', 'email', 'phone', 'height', 'weight', 'date_of_birth', 'bmi', 'internal_notes'] as const;

export function profileUpdatePayloadToFormData(payload: ProfileUpdateFormPayload): FormData {
  const fd = new FormData();
  fd.append('customer_id', String(payload.customer_id));

  const first_name = (payload.first_name ?? payload.name)?.toString().trim();
  if (first_name) {
    fd.append('first_name', first_name);
  }
  const last_name = payload.last_name != null ? String(payload.last_name).trim() : '';
  if (last_name !== '') {
    fd.append('last_name', last_name);
  }
  const full_name =
    payload.name != null && String(payload.name).trim() !== ''
      ? String(payload.name).trim()
      : [first_name, last_name].filter(Boolean).join(' ').trim();
  if (full_name !== '') {
    fd.append('name', full_name);
  }

  const gender = payload.gender;
  if (gender === 'male' || gender === 'female') {
    fd.append('gender', gender);
  }

  let prefs: Record<string, unknown> = {};
  if (payload.preferences != null && String(payload.preferences) !== '') {
    try {
      const parsed = JSON.parse(String(payload.preferences));
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        prefs = parsed as Record<string, unknown>;
      }
    } catch {
      prefs = { preferences_legacy: payload.preferences };
    }
  }

  for (const k of DASHBOARD_PREF_KEYS) {
    const v = payload[k];
    if (v !== undefined && v !== null && v !== '') {
      if (k === 'email') {
        prefs.mail = v;
      } else {
        prefs[k] = v;
      }
    }
  }
  if (payload.mail != null && payload.mail !== '') {
    prefs.mail = payload.mail;
  }

  const reserved = new Set([
    'customer_id',
    'name',
    'first_name',
    'last_name',
    'gender',
    'preferences',
    'picture',
    ...DASHBOARD_PREF_KEYS,
  ]);
  for (const [k, v] of Object.entries(payload)) {
    if (reserved.has(k)) {
      continue;
    }
    if (v !== undefined && v !== null) {
      prefs[k] = v;
    }
  }

  if (Object.keys(prefs).length > 0) {
    fd.append('preferences', JSON.stringify(prefs));
  }

  return fd;
}

/**
 * Fetch profile for customer and sync the customer object to the auth store.
 * This ensures user.allergies and user.dislikes (and rest of profile) are up to date.
 * Call from ProfileScreen on load and from AllergiesDislikesScreen when opening so selected items come from profile.
 */
export async function fetchProfileAndSyncUser(customer_id: number): Promise<any> {
  const res = await apiGet<ProfileResponse>(endpoints.profile, { customer_id });
  const customer_raw = (res as any)?.data?.customer ?? (res as any)?.customer;
  if (!customer_raw || typeof customer_raw !== 'object') {return res;}

  const prev = getAuthStore().user;
  const allergy_raw = coalesceAllergyRaw(customer_raw);
  const dislike_raw = coalesceDislikeRaw(customer_raw);
  const allergies = normalizeAllergies(allergy_raw);
  const dislikes = normalizeDislikes(dislike_raw);
  const allergy_ids = allergies.map(a => a.id);
  const dislike_ids = dislikes.map(d => d.id);
  const inventory_ids_raw = customer_raw?.inventory_ids ?? customer_raw?.customer_selected_inventory_ids;
  const inventory_ids = Array.isArray(inventory_ids_raw)
    ? inventory_ids_raw
        .map((value: unknown) => parseIdFromRaw(value))
        .filter((value): value is number => value != null)
    : Array.isArray(customer_raw?.customer_selected_items)
      ? customer_raw.customer_selected_items
          .map((value: unknown) =>
            typeof value === 'number' || typeof value === 'string'
              ? parseIdFromRaw(value)
              : parseIdFromRaw((value as any)?.id ?? (value as any)?.inventory_id),
          )
          .filter((value: number | null | undefined): value is number => value != null)
      : prev?.inventory_ids;

  const mail_resolved = resolveMailFromCustomerPayload(customer_raw) || (prev?.mail != null ? String(prev.mail) : '');
  const phone_resolved = resolvePhoneFromCustomerPayload(customer_raw) || (prev?.phone != null ? String(prev.phone) : '');

  const customer: User = {
    ...(prev ?? (customer_raw as User)),
    ...customer_raw,
    first_name: resolveProfileFirstName(customer_raw),
    last_name: resolveProfileLastName(customer_raw),
    mail: mail_resolved,
    ...(phone_resolved !== '' ? { phone: phone_resolved } : {}),
    session_token: (prev?.session_token ?? (customer_raw as any).session_token) as string,
    allergies,
    dislikes,
    allergy_ids,
    dislike_ids,
    ...(Array.isArray(inventory_ids) ? { inventory_ids } : {}),
  };

  getAuthStore().setUser(customer);
  try {
    await AsyncStorage.setItem(CURRENT_USER_KEY, JSON.stringify(customer));
  } catch {
    // ignore persistence errors
  }
  return res;
}
